import React, { useState } from 'react';
import {
  Box,
  ToggleButton,
  Popover,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useSelector, useDispatch } from 'react-redux';
import {
  setPhysics,
  setPhysicsSetting,
  resetPhysicsSettings,
  setSelfReference,
  setSnapToGrid,
  setOffsetPairs,
  setPairOffset,
  setDisplayMode,
  setDefaultNodeShape,
} from '../../features/networkSlice';
import { PHYSICS_DEFAULTS } from '../../lib/network/constants';
import { PAIR_OFFSET } from '../../lib/geometry/edges';
import {
  DISPLAY_ID, DISPLAY_LABEL, SHAPES_FOR_MODE, defaultShapeForMode,
} from '../../lib/display';

const PHYSICS_FIELDS = [
  { key: 'charge', label: 'Repulsion', step: 100 },
  { key: 'linkStrength', label: 'Spring', step: 0.05 },
  { key: 'linkDistance', label: 'Distance', step: 10 },
];

export default function Settings() {
  const dispatch = useDispatch();
  const physics = useSelector((state) => state.network.physics);
  const physicsSettingsFromState = useSelector((state) => state.network.physicsSettings);
  const physicsSettings = { ...PHYSICS_DEFAULTS, ...(physicsSettingsFromState || {}) };
  const selfReference = useSelector((state) => state.network.selfReference);
  const snapToGrid = useSelector((state) => state.network.snapToGrid);
  const offsetPairs = useSelector((state) => state.network.offsetPairs !== false);
  const pairOffset = useSelector((state) => (
    Number.isFinite(state.network.pairOffset) ? state.network.pairOffset : PAIR_OFFSET
  ));
  const displayMode = useSelector((state) => (
    state.network.displayMode === DISPLAY_ID ? DISPLAY_ID : DISPLAY_LABEL
  ));
  const storedShape = useSelector((state) => state.network.defaultNodeShape);
  const defaultNodeShape = defaultShapeForMode(displayMode, storedShape);
  const shapeOptions = SHAPES_FOR_MODE[displayMode];

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handlePhysicsSettingChange = (key) => (event) => {
    const { value } = event.target;
    if (value === '' || value === '-') return;
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      dispatch(setPhysicsSetting({ key, value: parsed }));
    }
  };

  return (
    <>
      <ToggleButton
        size="small"
        value="settings"
        selected={open}
        title="Settings"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <SettingsOutlinedIcon fontSize="small" />
      </ToggleButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 360 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2">Settings</Typography>
            <FormControl size="small">
              <InputLabel id="display-mode-label">Node display</InputLabel>
              <Select
                labelId="display-mode-label"
                label="Node display"
                value={displayMode}
                onChange={(e) => dispatch(setDisplayMode(e.target.value))}
              >
                <MenuItem value={DISPLAY_ID}>ID mode</MenuItem>
                <MenuItem value={DISPLAY_LABEL}>Label mode</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel id="default-shape-label">Default shape</InputLabel>
              <Select
                labelId="default-shape-label"
                label="Default shape"
                value={defaultNodeShape}
                onChange={(e) => dispatch(setDefaultNodeShape(e.target.value))}
              >
                {shapeOptions.map((option) => (
                  <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider />
            <FormControlLabel
              control={<Switch />}
              label="Self effects"
              title="Show/hide self effects"
              onChange={() => dispatch(setSelfReference(!selfReference))}
              checked={selfReference}
            />
            <FormControlLabel
              control={<Switch />}
              label="Snap grid"
              title="Enable grid snapping and display"
              onChange={() => dispatch(setSnapToGrid(!snapToGrid))}
              checked={snapToGrid}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Switch />}
                label="Offset pairs"
                title="Shift opposite links so both stay visible"
                onChange={() => dispatch(setOffsetPairs(!offsetPairs))}
                checked={offsetPairs}
                sx={{ mr: 0, flex: 1 }}
              />
              {offsetPairs && (
                <TextField
                  label="Gap"
                  type="number"
                  size="small"
                  title="Positive and negative sit on opposite sides"
                  value={pairOffset}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (value === '' || value === '-') return;
                    const parsed = Number(value);
                    if (!Number.isNaN(parsed)) dispatch(setPairOffset(parsed));
                  }}
                  inputProps={{ step: 1 }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 88 }}
                />
              )}
            </Stack>
            <FormControlLabel
              control={<Switch />}
              label="Auto layout"
              title="Let a force-directed layout arrange the nodes"
              onChange={() => dispatch(setPhysics(!physics))}
              checked={physics}
            />
            {physics && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} alignItems="center">
                  {PHYSICS_FIELDS.map((field) => (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type="number"
                      size="small"
                      value={physicsSettings[field.key]}
                      onChange={handlePhysicsSettingChange(field.key)}
                      inputProps={{ step: field.step }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 104 }}
                    />
                  ))}
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => dispatch(resetPhysicsSettings())}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Reset physics
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
